import { reactive, watch } from 'vue';
import BasePagedListFilterSetupService from '@/setup/BasePagedListFilterSetupService';
import { MOBILITY } from '@/typings/enums/MOBILITY';
import { USER_STATUS } from '@/typings/enums/USER_STATUS';
import useCandidateService from '@/services/CandidateService';
import useTempWorkerPagedListFilterService from '@/services/temp-worker/TempWorkerPagedListFilterService';
import useLanguageService from '@/services/LanguageService';
import useProfessionalSkillService from '@/services/ProfessionalSkillService';
import useTalentPoolService from '@/services/TalentPoolService';
import useQualificationService from '@/services/QualificationService';

type Props = SetupProps<typeof TempWorkerPagedListFilterSetupService.props>

export default class TempWorkerPagedListFilterSetupService
	extends BasePagedListFilterSetupService<
		Props,
		Armado.TempWorkerPagedListParametersExtended,
		(Armado.TempWorkerListItemEntityType | IOrderUserAgencyListItemModel)
	> {

	static readonly props = { aside: Boolean };

	protected filterService = useTempWorkerPagedListFilterService();
	readonly candidateService = useCandidateService();

	private readonly state = reactive({
		isLoading: false,
		languageOptions: [] as ILanguageModel[],
		skillOptions: [] as IProfessionalSkillModel[],
		groupOptions: [] as ITalentPoolForProfileModel[],
		agencyQualificationsOptions: [] as IQualificationForListModel[],
		inModal: false,
		disabled: [] as string[],
	});

	readonly mobilityOptions = [
		{
			label: `Moins de 10km`,
			value: MOBILITY.Lte10Km,
		},
		{
			label: `Moins de 30km`,
			value: MOBILITY.Lte30Km,
		},
		{
			label: `Moins de 50km`,
			value: MOBILITY.Lte50Km,
		},
		{
			label: `Plus de 50km`,
			value: MOBILITY.Gte50Km,
		},
	];

	readonly statusOptions = [
		{
			label: `Intérimaires activés`,
			value: USER_STATUS.Activated,
		},
		{
			label: `Intérimaires non activés`,
			value: USER_STATUS.Inactivated,
		},
		{
			label: `Intérimaires non dématérialisés`,
			value: USER_STATUS.Created,
		},
		{
			label: `Intérimaires dont l'email est à confirmer`,
			value: USER_STATUS.HasToConfirmedEmail,
		},
	];

	readonly notes = [
		0,
		1,
		2,
		3,
		4,
		5,
	];

	get disabled () { return this.state.disabled;}
	get isLoading () { return this.state.isLoading;}
	get languageOptions () { return this.state.languageOptions;}
	get skillOptions () { return this.state.skillOptions;}
	get groupOptions () { return this.state.groupOptions;}
	get inModal () { return this.state.inModal;}

	get filteredGroupOptions () {
		return this.groupOptions.filter(x => x.id !== this.filter?.talentPoolIdToExclude);
	}

	get filteredAgencyQualificationsOptions () {
		return this.filter?.agencyCompanyId
			? this.state.agencyQualificationsOptions.filter(x => x.agencyCompanyId === this.filter?.agencyCompanyId)
			: this.state.agencyQualificationsOptions;
	}

	constructor (props: Props, _aside?: OrionAside) {
		super(props, _aside);

		this.onBeforeMountAsync = async () => {
			this.state.isLoading = true;
			this.state.languageOptions = await useLanguageService().getLanguageListAsync();
			this.state.skillOptions = await useProfessionalSkillService().listAsync();
			this.state.groupOptions = await useTalentPoolService().listAsync();
			this.state.agencyQualificationsOptions = await useQualificationService().listAsync();
			this.state.isLoading = false;
			this.state.inModal = this.filterService.inModal;

			if (this.candidateService.inOrder() && this.filterService.inModal) {
				if (this.filterService.filter.agencyCompanyId)
					this.state.disabled.push('agencyCompanyId');
			}
			if (this.filterService.filter.talentPoolIds?.length) {
				this.state.disabled.push('talentPoolIds');
			} else {
				this.state.disabled.delete('talentPoolIds');
			}
		};

		watch(() => this.filter.agencyCompanyId, (val, oldVal) => {
			if (this.candidateService.inOrder() && this.filterService.inModal && val === undefined) {
				this.filter.agencyCompanyId = oldVal;
			}
		});
	}


	toggleRateFilter (value: number) {
		if (this.filter.rating === value) {
			this.filter.rating = undefined;
			return;
		}
		this.filter.rating = value;
	}
}
